import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { SeoHead } from "@/components/SeoHead";
import { absoluteSeoUrl, limitSeoText } from "@/lib/seo";

const noIndexPaths = [
  "/account",
  "/admin",
  "/auth",
  "/cart",
  "/checkout",
  "/feedback",
  "/forgot-password",
  "/login",
  "/order",
  "/register",
  "/track",
  "/wishlist",
];

const titleCase = (value) => value
  .split("-")
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(" ");

const isNoIndexPath = (pathname) => noIndexPaths.some((path) => {
  return pathname === path || pathname.startsWith(`${path}/`);
});

const shopCanonical = (params) => {
  const selected = new URLSearchParams();
  ["category", "brand", "tag"].forEach((key) => {
    if (params.get(key)) {
      selected.set(key, params.get(key));
    }
  });
  const query = selected.toString();
  return `/shop${query ? `?${query}` : ""}`;
};

const getRouteSeo = (pathname, search) => {
  const params = new URLSearchParams(search);
  const canonicalPath = pathname === "/shop" ? shopCanonical(params) : pathname;
  const base = {
    title: "Eira Products | Natural Everyday Essentials in India",
    description: "Shop natural personal care, wellness, kitchen and home essentials for Indian "
      + "homes.",
    canonical: absoluteSeoUrl(canonicalPath),
    type: "website",
  };

  if (pathname === "/") {
    return base;
  }
  if (pathname === "/shop") {
    const category = params.get("category");
    const brand = params.get("brand");
    const label = titleCase(category || brand || "");
    return {
      ...base,
      title: label
        ? `${label} Products | Natural Everyday Essentials | Eira Products`
        : "Shop Natural Skincare, Wellness & Everyday Essentials | Eira Products",
      description: label
        ? `Explore ${label} products from Eira Products, curated for thoughtful Indian homes.`
        : "Shop natural skincare, personal care, wellness, food, kitchen and home essentials "
          + "online in India.",
    };
  }
  if (pathname === "/corporate-gifts") {
    return {
      ...base,
      title: "Corporate Gifts & Custom Gift Boxes | Eira Products",
      description: "Create thoughtful corporate gifts and curated Eira gift boxes for teams, "
        + "clients and celebrations.",
    };
  }
  if (pathname === "/privacy-policy") {
    return {
      ...base,
      title: "Privacy Policy | Eira Products",
      description: "Learn how Eira Products collects, uses, retains and deletes customer data.",
    };
  }
  if (pathname === "/terms-and-conditions") {
    return {
      ...base,
      title: "Terms & Conditions | Eira Products",
      description: "Read Eira Products purchase, cancellation, returns and account-use terms.",
    };
  }
  if (pathname.startsWith("/brand/")) {
    return {
      ...base,
      title: "Brand Collection | Eira Products",
      description: "Explore a curated Eira brand collection of natural everyday essentials for "
        + "Indian homes.",
    };
  }
  if (pathname.startsWith("/campaign/")) {
    return {
      ...base,
      title: "Featured Collection | Eira Products",
      description: "Discover a featured Eira collection of thoughtful essentials for everyday "
        + "living.",
    };
  }
  if (pathname.startsWith("/page/")) {
    return {
      ...base,
      title: "Eira Products | Everyday Essentials in India",
    };
  }
  if (pathname.startsWith("/product/")) return null;
  if (isNoIndexPath(pathname)) {
    return {
      ...base,
      title: "Eira Products",
      description: "Eira Products customer account and order services.",
      noIndex: true,
    };
  }
  return {
    ...base,
    title: "Page Not Found | Eira Products",
    description: limitSeoText("The requested Eira Products page could not be found."),
    noIndex: true,
  };
};

export const RouteSeo = () => {
  const location = useLocation();
  const seo = useMemo(() => {
    return getRouteSeo(location.pathname, location.search);
  }, [location.pathname, location.search]);

  return <SeoHead seo={seo} />;
};